// ============================================================
// A build stroke: pointer down, move, up - and the wheel in between.
//
// The drag itself lives in a ref (it changes on every pointer move and
// nothing renders from it directly); `preview` is the ghost the panel
// draws from it. What a stroke builds is decided elsewhere: `begin`
// turns the first cell into the drag, `commit` turns the finished drag
// into a terrain edit.
// ============================================================
import { useState, useCallback, useRef } from 'react';
import { unprojectToPlane, SCENE_VIEW } from '../engine/battlefield/projection';
import { clampToBoundary } from '../engine/battlefield/boundary';
import { gestureFootprint, extendStroke } from '../engine/battlefield/buildGesture';
import { pointerToScene } from '../utils/scenePointer';

/** The ghost the panel draws for a drag. */
const previewOf = (d) => ({ cells: gestureFootprint(d), erasing: d.erasing, level: d.level, layers: d.layers });

/** The cell under the pointer, clamped into the arena, or null off the ground. */
export function useCellUnder(getCamera, boundary, tile) {
  return useCallback(
    (e) => {
      const p = pointerToScene(e);
      const ground = unprojectToPlane(p.x, p.y, 0, getCamera(), SCENE_VIEW);
      if (!ground) return null;
      const inside = clampToBoundary(boundary, ground.x, ground.z);
      return { x: Math.floor(inside.x / tile), z: Math.floor(inside.z / tile) };
    },
    [getCamera, boundary, tile],
  );
}

/**
 * The pointer and wheel handlers for one stroke at a time.
 *
 * `begin(e, cell)` returns the new drag; `commit(drag, footprint)` applies
 * a finished one with a non-empty footprint; `setHover` follows the
 * pointer whether or not a drag is live.
 */
export function useBuildStroke({ cellUnder, begin, commit, setHover, settings }) {
  const drag = useRef(null);
  const [preview, setPreview] = useState(null);
  const start = useCallback(
    (e) => {
      const cell = cellUnder(e);
      if (!cell) return false;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      drag.current = begin(e, cell);
      setPreview(previewOf(drag.current));
      return true;
    },
    [cellUnder, begin],
  );
  const move = useStrokeMove({ drag, setPreview, cellUnder, setHover });
  const finish = useStrokeFinish({ drag, setPreview, commit });
  const wheel = useStrokeWheel({ drag, setPreview, settings });
  // Esc (plan map-display, F; the user's Build ruling): the drag is thrown away and nothing is built. The
  // pointer's up then finds no drag, and the capture ends with it.
  const cancel = useCallback(() => {
    drag.current = null;
    setPreview(null);
  }, []);
  return { preview, start, move, finish, wheel, cancel };
}

function useStrokeMove({ drag, setPreview, cellUnder, setHover }) {
  return useCallback(
    (e) => {
      const cell = cellUnder(e);
      if (cell) setHover(cell);
      const d = drag.current;
      if (!d || d.id !== e.pointerId || !cell) return;
      d.painted = d.gesture === 'brush' ? extendStroke(d, cell) : d.painted;
      d.to = cell;
      setPreview(previewOf(d));
    },
    [drag, setPreview, cellUnder, setHover],
  );
}

function useStrokeFinish({ drag, setPreview, commit }) {
  return useCallback(
    (e) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      drag.current = null;
      setPreview(null);
      const footprint = gestureFootprint(d);
      if (footprint.length) commit(d, footprint);
    },
    [drag, setPreview, commit],
  );
}

/**
 * The wheel, while a build drag is live, is the LEVEL it builds at.
 *
 * The same rule as carrying a marker (v7.25.0): the thing you want to
 * adjust is the thing you are looking at, and reaching for a number field
 * mid-drag is not possible anyway. Returns true when it took the event,
 * so the panel knows not to zoom.
 */
function useStrokeWheel({ drag, setPreview, settings }) {
  const { floatMode, setLevel, setLayers } = settings;
  return useCallback(
    (e) => {
      const d = drag.current;
      if (!d) return false;
      const up = e.deltaY < 0 ? 1 : -1;
      // Shift is a MOMENTARY override of the toggle, either way round, so
      // the rarer of the two is always one modifier away.
      if (e.shiftKey ? !floatMode : floatMode) {
        d.level += up;
        setLevel(d.level);
      } else {
        d.layers = Math.max(1, d.layers + up);
        setLayers(d.layers);
      }
      // The ghost has to follow, or the numbers change invisibly - which
      // from the top-down view, where building happens, means they change
      // with no cue at all.
      setPreview(previewOf(d));
      return true;
    },
    [drag, setPreview, floatMode, setLevel, setLayers],
  );
}
